import React from 'react';

import { FormControl, FormControlLabel, FormHelperText } from '../Form';
import { InputLabel } from '../Input';
import { Select } from '../Select';
import { MenuItem } from '../MenuItem';
import { ToggleButtonGroup } from '../ToggleButtonGroup';
import { ToggleButton } from '../ToggleButton';
import { Box } from '../Box';
import { Radio, RadioGroup } from '../Radio';
import parseColor from '../util/parseColor';
import { styled } from '../styled';

const JoinedToggleButtonGroup = styled(ToggleButtonGroup)(({ theme, color }) => ({
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    padding: theme.spacing(1, 2.75),
    border: `1px solid ${theme.palette.grey[300]} !important`,
    '&:not(.Mui-selected)': {
      background: theme.palette.grey[50],
      '&:hover': {
        background: theme.palette.grey[200],
      },
    },
    '& .MuiSvgIcon-root': {
      fill: parseColor(theme, color, 'light'),
    },
    '&.Mui-selected': {
      color: parseColor(theme, color, 'dark'),
      '& .MuiSvgIcon-root': {
        fill: parseColor(theme, color, 'dark'),
      },
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1, 1.75),
      fontSize: '14px',
      border: `1px solid ${theme.palette.grey[300]} !important`,
      borderRadius: '4px !important',
      margin: `${theme.spacing(0, 1, 1, 0)} !important`,
      '&.Mui-selected': {
        border: '1px solid !important',
      },
    },
  },
}));

function JoinedButtonContainer(props) {
  const {
    name,
    value,
    onChange,
    multiple,
    label,
    color,
    size,
    children,
    inputProps,
  } = props;
  const handleChange = (e, next) => {
    onChange({ ...e, target: { ...e.target, value: next } });
    if (!next) inputProps.onBlur(); // show required immediately on deselect
  };
  return (
    <JoinedToggleButtonGroup
      name={name}
      value={value}
      onChange={handleChange}
      exclusive={!multiple}
      color={color}
      size={size}
      aria-label={label}
      {...inputProps}
    >
      {children}
    </JoinedToggleButtonGroup>
  );
}

const SplitToggleButtonGroup = styled(JoinedToggleButtonGroup)(({ theme }) => ({
  flexWrap: 'wrap',
  '& .MuiToggleButton-root': {
    width: '128px',
    borderRadius: '8px !important',
    border: `1px solid ${theme.palette.grey[300]}!important`,
    padding: theme.spacing(1),
    '&:not(.Mui-selected)': {
      background: theme.palette.grey[50],
    },
    '&:not(:last-of-type)': {
      marginRight: theme.spacing(2),
    },
    '&.Mui-selected': {
      border: '1px solid !important',
    },
    [theme.breakpoints.down('lg')]: {
      margin: `${theme.spacing(0, 2, 2, 0)}!important`,
    },
  },
}));

function SplitButtonContainer(props) {
  const {
    name,
    value,
    onChange,
    multiple,
    label,
    color,
    size,
    children,
    inputProps,
  } = props;
  const handleChange = (e, next) => {
    onChange({ ...e, target: { ...e.target, value: next } });
    if (!next) inputProps.onBlur(); // show required immediately on deselect
  };
  return (
    <SplitToggleButtonGroup
      name={name}
      value={value}
      onChange={handleChange}
      exclusive={!multiple}
      color={color}
      size={size}
      aria-label={label}
      {...inputProps}
    >
      {children}
    </SplitToggleButtonGroup>
  );
}

function BulletButtonContainer(props) {
  const {
    name,
    value,
    onChange,
    label,
    color,
    size,
    children,
    inputProps,
  } = props;
  const handleChange = (e, next) => {
    onChange({ ...e, target: { ...e.target, value: next } });
    if (!next) inputProps.onBlur(); // show required immediately on deselect
  };
  return (
    <RadioGroup
      name={name}
      value={value}
      onChange={handleChange}
      color={color}
      size={size}
      aria-label={label}
      {...inputProps}
    >
      {children}
    </RadioGroup>
  );
}

function SelectField({
  input = {},
  label,
  data,
  children,
  required,
  multiple,
  helperText,
  inputLabelProps,
  formControlProps = {},
  formHelperTextProps,
  fullWidth,
  meta,
  color = 'primary',
  size,
  options,
  native = true,
  buttons, // 'split' | 'joined' | 'radio'
  ...custom
}) {
  const {
    name,
    value,
    onChange,
    ...restInput
  } = input;
  const { touched, error } = meta || {};
  const finalValue = multiple && !value ? [] : value;
  const hasError = Boolean(touched && error);
  const labelId = `select-input-${name}`;

  const SelectContainer = ({
    joined: JoinedButtonContainer,
    split: SplitButtonContainer,
    bullet: BulletButtonContainer,
  }[buttons]) || Select;

  const selectOptions = (() => {
    if (!options) return children;
    if (buttons === 'joined' || buttons === 'split') {
      return (
        options.map((op) => (
          <ToggleButton key={op.value} value={op.value}>
            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
              {op.icon}
              {op.label}
            </Box>
          </ToggleButton>
        ))
      );
    }
    if (buttons === 'bullet') {
      return (
        options.map((op) => (
          <FormControlLabel
            key={op.value}
            value={op.value}
            control={<Radio />}
            label={op.label}
          />
        ))
      );
    }
    if (!native) {
      return (
        [{ label: '', value: '' }, ...options].map((op) => (
          <MenuItem key={op.value} value={op.value}>
            {op.label}
          </MenuItem>
        ))
      );
    }
    return (
      [{ label: '', value: '' }, ...options].map((op) => (
        <option key={op.value} value={op.value}>
          {op.label}
        </option>
      ))
    );
  })();

  return (
    <FormControl
      required={required}
      variant={custom.variant}
      color={color}
      size={size}
      error={hasError}
      {...formControlProps}
      sx={{ ...formControlProps.sx, width: (fullWidth ? '100%' : undefined) }}
    >
      {(label && !buttons) && (
        <InputLabel
          id={labelId}
          {...inputLabelProps}
          color={color}
          size={size}
          error={hasError}
        >
          {label}
        </InputLabel>
      )}
      <SelectContainer
        name={name}
        value={finalValue}
        onChange={onChange}
        multiple={multiple}
        label={label}
        labelId={labelId}
        inputProps={{ required, ...restInput }}
        native={native}
        color={color}
        size={size}
        error={hasError}
        {...custom}
      >
        {selectOptions}
      </SelectContainer>
      {(helperText || hasError) && (
        <FormHelperText
          error={hasError}
          {...formHelperTextProps}
        >
          {hasError ? error : helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default SelectField;
